import { useEffect } from 'react';
import { childrenOf, type EditorApi } from './editor.tsx';
import { getState, setState } from './store.ts';

/**
 * Dragging blocks about.
 *
 * Built on plain mouse events rather than the browser's own drag-and-drop:
 * HTML5 dragging never started from a handle sitting next to a page full of
 * editable lines, so the handle looked dead. Tracking the pointer directly
 * also means the drop line can be drawn wherever it should actually land.
 */

/** The block under a point, or the nearest one above or below it. */
export function blockAt(x: number, y: number): string | null {
  const direct = document.elementFromPoint(x, y)?.closest('[data-block]');
  if (direct) return direct.getAttribute('data-block');

  let best: Element | null = null;
  let closest = Infinity;
  for (const el of document.querySelectorAll('[data-block]')) {
    const r = el.getBoundingClientRect();
    const gap = y < r.top ? r.top - y : y > r.bottom ? y - r.bottom : 0;
    if (gap < closest) { closest = gap; best = el; }
  }
  return best?.getAttribute('data-block') ?? null;
}

type Armed = { ids: string[]; x: number; y: number };

/** Set on mousedown; a drag only begins once the pointer actually moves. */
let armed: Armed | null = null;

export function armBlockDrag(ids: string[], x: number, y: number): void {
  armed = { ids, x, y };
}

export function useBlockDrag(api: EditorApi): void {
  useEffect(() => {
    const finish = () => {
      armed = null;
      document.body.classList.remove('dragging');
      setState({ dragIds: [], dragOverId: null, dragWhere: null });
    };

    const onMove = (e: MouseEvent) => {
      if (!armed) return;

      if (!getState().dragIds.length) {
        // A press that never travels is a click on the handle, not a drag.
        if (Math.abs(e.clientX - armed.x) + Math.abs(e.clientY - armed.y) < 5) return;
        setState({ dragIds: armed.ids });
        document.body.classList.add('dragging');
      }
      e.preventDefault();

      const overId = blockAt(e.clientX, e.clientY);
      if (!overId || getState().dragIds.includes(overId)) {
        setState({ dragOverId: null, dragWhere: null });
        return;
      }
      const el = document.querySelector(`[data-block="${CSS.escape(overId)}"]`);
      const box = el?.getBoundingClientRect();
      if (!box) return;
      setState({
        dragOverId: overId,
        dragWhere: e.clientY < box.top + box.height / 2 ? 'above' : 'below',
      });
    };

    const onUp = () => {
      const { dragIds, dragOverId, dragWhere } = getState();
      if (!dragIds.length || !dragOverId) { finish(); return; }

      const target = api.blocks.find((b) => b.id === dragOverId);
      if (!target) { finish(); return; }

      const siblings = childrenOf(api.blocks, target.parent_id);
      const at = siblings.findIndex((b) => b.id === target.id);
      const afterId = dragWhere === 'above' ? (siblings[at - 1]?.id ?? null) : target.id;

      api.moveMany(dragIds, target.parent_id, afterId);
      finish();
    };

    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') finish(); };

    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('mouseup', onUp, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('mouseup', onUp, true);
      document.removeEventListener('keydown', onKey, true);
      finish();
    };
  }, [api]);
}
